import { type ReactElement, type MouseEvent as ReactMouseEvent } from 'react';
import { NeonButtonFooter, NeonModalDialog } from '@ps-refarch-ux/neon';
import { NeonButtonTypes } from '@/constants/neon-constants';
import styles from './delete-recommendation-modal.module.css';

export interface DeleteRecommendationModalProps {
  isOpen: boolean;
  modalId: string;
  courseName: string;
  isDeleting: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

/** Neon confirmation modal for permanently deleting a course recommendation. */
export function DeleteRecommendationModal({
  isOpen,
  modalId,
  courseName,
  isDeleting,
  onClose,
  onConfirm,
}: DeleteRecommendationModalProps): ReactElement | null {
  if (!isOpen) {
    return null;
  }

  // Ignore dismissals while the delete request is in flight so the modal stays locked.
  const handleClose = () => {
    if (isDeleting) {
      return;
    }
    onClose();
  };

  return (
    <NeonModalDialog id={modalId} neonDialogHasClosed={handleClose} dataSize="small">
      <div data-slot="dialog-header-title">Delete recommendation?</div>
      <div
        className={styles.body}
        data-slot="dialog-body"
        aria-label={`Delete recommendation?: ${courseName}. Deleting this course recommendation permanently removes it from all students who received it. This action cannot be undone.`}
      >
        <p className={`__neon__text ${styles.bodyText}`}>
          Deleting this course recommendation permanently removes it from all students who received
          it. This action cannot be undone.
        </p>
      </div>
      <div data-slot="dialog-button-footer">
        <NeonButtonFooter
          id="delete-recommendation-modal-footer"
          buttons={[
            {
              id: 'delete-recommendation-cancel',
              text: 'Do not delete',
              type: NeonButtonTypes.Borderless,
              disabled: isDeleting,
              onClick: (event: Event | ReactMouseEvent) => {
                const target = (event?.target ?? (event as ReactMouseEvent)?.currentTarget) as
                  | HTMLElement
                  | undefined;
                target?.dispatchEvent(new Event('neonDialogClose', { bubbles: true }));
              },
            },
          ]}
          primaryButton={{
            id: 'delete-recommendation-confirm',
            text: 'Delete recommendation',
            type: NeonButtonTypes.Tertiary,
            disabled: isDeleting,
            isLoading: isDeleting,
            onClick: onConfirm,
          }}
        />
      </div>
    </NeonModalDialog>
  );
}
